import { ScreenActivity, ActivityModel, Activity } from './activity';
import {
  DataEntity,
  ParentReference
} from '../../models/data-entities/data-entity';
import { OnChanges, SimpleChanges, Type } from '@angular/core';
import { DisplayEntityValueDataEntity } from '../data-entities';
import { DataEntityFactory } from '../../services';
import { ConditionTarget } from 'src/app/components/system/condition-builder/condition-builder.model';
import { map, Observable, of } from 'rxjs';

export enum FormLayoutModes {
  SingleColumn = 0,
  TwoColumn = 1
}

export class DataEntityLayoutModel {
  type: string;
  entity: DataEntity;
  entityTemplateCode: string;
  displayOrder: number;
  sectionId: string;

  constructor(options?: Partial<DataEntityLayoutModel>) {
    if (options) {
      Object.assign(this, options);
    }

    if (options?.entity) {
      this.entity = DataEntityFactory.createDataEntity(
        options.entity.dataEntityTypeCode,
        options.entity
      );
    }

  }

  getDataEntities(): DataEntity[] {
    if (this.entity) {
      return [this.entity];
    }
    else {
      return [];
    }
  }
}

export class ConditionalDataEntityLayoutModel extends DataEntityLayoutModel {
  visibleCriteria: ConditionTarget;
  requiredCriteria: ConditionTarget;

  isRequired?: boolean = false;
  isVisible?: boolean = true;

  constructor(options?: Partial<ConditionalDataEntityLayoutModel>) {
    super(options);
    if (options) {
      Object.assign(this, options);
    }

  }
}

export class EntityTableRowLayout {
  visibleCriteria?: ConditionTarget;
  backgroundColor?: string;
  cells: EntityTableCelllayout[];
  isVisible?: boolean = true;
}

export class EntityTableCelllayout {
  type: 'text' | 'entity' | 'layout' = 'text';
  staticText?: string;
  entity?: DataEntity;
  layoutModel?: DataEntityLayoutModel;
  showEditor?: boolean = false;
  colspan?: number = 1;
  rowSpan?: number = 1;
  backgroundColor?: string;
  renderListItemsAsDynamicColumns?: boolean = false;
}

export class DataEntityTableLayoutModel extends ConditionalDataEntityLayoutModel {
  rows: EntityTableRowLayout[];
  sameActivityDataEntities: string[] = [];
  isVisible: boolean = true;

  evaluateIsVisible(entities: DataEntity[], activity: Activity<ActivityModel>): Observable<boolean> {
    if (this.visibleCriteria) {
      DataEntity.prepareDependentEntities(activity);
      return DataEntity.evalCriteria(entities, activity.preparedDependentEntitiesMap, this.visibleCriteria.group).pipe(map(isVisible => {
        this.isVisible = isVisible;
        return isVisible;
      }));
    }
    return of(this.isVisible);
  }

  evaluateRowIsVisible(row: EntityTableRowLayout, entities: DataEntity[], activity: Activity<ActivityModel>): Observable<boolean> {
    if (row?.visibleCriteria) {
      DataEntity.prepareDependentEntities(activity);
      return DataEntity.evalCriteria(entities, activity.preparedDependentEntitiesMap, row.visibleCriteria.group).pipe(map(isVisible => {
        row.isVisible = isVisible;
        return isVisible;
      }));
    }
    if (row) {
      row.isVisible = row.isVisible !== false;
    }
    return of(row?.isVisible !== false);
  }

  constructor(options?: Partial<DataEntityTableLayoutModel>) {
    super(options);
    if (options) {
      Object.assign(this, options);
    }

    this.type = this.type || 'table-data-entity-layout';

    this.rows = this.rows || [];
    if (this.rows) {
      this.rows.forEach(row => {
        if (row.cells) {
          row.cells.forEach(cell => {
            if (cell.entity) {
              cell.entity = DataEntityFactory.createDataEntity(cell.entity.dataEntityTypeCode, cell.entity);
            }

            // Convert layoutModel to the correct TypeScript class based on type
            if (cell.layoutModel) {
              if (cell.layoutModel.type === 'table-data-entity-layout') {
                cell.layoutModel = new DataEntityTableLayoutModel(cell.layoutModel as DataEntityTableLayoutModel);
              } else if (cell.layoutModel.type === 'group-data-entity-layout') {
                cell.layoutModel = new DataEntityGroupLayoutModel(cell.layoutModel as DataEntityGroupLayoutModel);
              } else if (cell.layoutModel.type === 'row-data-entity-layout') {
                cell.layoutModel = new DataEntityRowLayoutModel(cell.layoutModel as DataEntityRowLayoutModel);
              } else {
                cell.layoutModel = new DataEntityLayoutModel(cell.layoutModel);
              }
            }
          });
        }
      });
    }
  }

  getDataEntities(): DataEntity[] {
    const entities: DataEntity[] = [];

    if (this.rows) {
      this.rows.forEach(row => {
        if (row.cells) {
          row.cells.forEach(cell => {
            if (cell.entity) {
              entities.push(cell.entity);
            }

            // Get entities from nested layout model if it exists
            if (cell.layoutModel) {
              const layoutEntities = cell.layoutModel.getDataEntities();
              if (layoutEntities) {
                entities.push(...layoutEntities);
              }
            }
          });
        }
      });
    }

    return entities;
  }
}

export class DataEntityGroupLayoutModel extends ConditionalDataEntityLayoutModel {
  groupEntities: DataEntityLayoutModel[];
  sameActivityDataEntities: string[] = [];
  isVisible: boolean = true;

  evaluateIsVisible(entities: DataEntity[], activity: Activity<ActivityModel>): Observable<boolean> {
    if (this.visibleCriteria) {
      DataEntity.prepareDependentEntities(activity);
      return DataEntity.evalCriteria(entities, activity.preparedDependentEntitiesMap, this.visibleCriteria.group).pipe(map(isVisible => {
        this.isVisible = isVisible;
        return isVisible;
      }));
    }
    return of(this.isVisible);
  }

  constructor(options?: Partial<DataEntityGroupLayoutModel>) {
    super(options);

    if (options) {
      Object.assign(this, options);
    }

    this.type = this.type || 'group-data-entity-layout';

    this.groupEntities = this.groupEntities?.map(ge => {
      if (ge.type === 'table-data-entity-layout') {
        return new DataEntityTableLayoutModel(ge as DataEntityTableLayoutModel);
      } else if (ge.type === 'group-data-entity-layout') {
        return new DataEntityGroupLayoutModel(ge as DataEntityGroupLayoutModel);
      } else if (ge.type === 'row-data-entity-layout') {
        return new DataEntityRowLayoutModel(ge as DataEntityRowLayoutModel);
      }
      else {
        return new DataEntityLayoutModel(ge);
      }
    }) || [];
  }

  getDataEntities(): DataEntity[] {
    let entities: DataEntity[] = [];
    if (this.groupEntities) {
      this.groupEntities.forEach(group => {
        entities = entities.concat(group.getDataEntities());
      });
    }
    return entities;
  }
}

export class DataEntityRowLayoutModel extends ConditionalDataEntityLayoutModel {
  rowEntities: DataEntityLayoutModel[];
  sameActivityDataEntities: string[] = [];
  isVisible: boolean = true;

  evaluateIsVisible(entities: DataEntity[], activity: Activity<ActivityModel>): Observable<boolean> {
    if (this.visibleCriteria) {
      DataEntity.prepareDependentEntities(activity);
      return DataEntity.evalCriteria(entities, activity.preparedDependentEntitiesMap, this.visibleCriteria.group).pipe(map(isVisible => {
        this.isVisible = isVisible;
        return isVisible;
      }));
    }
    return of(this.isVisible);
  }

  constructor(options?: Partial<DataEntityRowLayoutModel>) {
    super(options);
    if (options) {
      Object.assign(this, options);
    }

    this.type = this.type || 'row-data-entity-layout';

    this.rowEntities = this.rowEntities?.map(re => {
      if (re.type === 'table-data-entity-layout') {
        return new DataEntityTableLayoutModel(re as DataEntityTableLayoutModel);
      } else if (re.type === 'group-data-entity-layout') {
        return new DataEntityGroupLayoutModel(re as DataEntityGroupLayoutModel);
      } else if (re.type === 'row-data-entity-layout') {
        return new DataEntityRowLayoutModel(re as DataEntityRowLayoutModel);
      }
      else {
        return new DataEntityLayoutModel(re);
      }
    }) || [];
  }
  getDataEntities(): DataEntity[] {
    let entities: DataEntity[] = [];
    if (this.rowEntities) {
      this.rowEntities.forEach(row => {
        entities = entities.concat(row.getDataEntities());
      });
    }
    return entities;
  }
}

export abstract class FormLayoutModel {
  abstract addLayout(layoutModel: DataEntityLayoutModel);
  static modeModels: { [key: number]: Type<FormLayoutModel> } = {};
  abstract getEntities(): DataEntity[];
  abstract addEntity(de: DataEntity);
  abstract removeEntity(de: DataEntityLayoutModel);
  abstract clearEntities();
  dependentEntityValues: { templateCode: string; dataEntityTypeCode: string, value: string }[];

  static getModeModel(
    activity: FormActivity,
    mode: FormLayoutModes,
    options?: Partial<FormLayoutModel>
  ) {
    if (FormLayoutModel.modeModels[mode]) {
      return new FormLayoutModel.modeModels[mode](activity, options);
    }
  }

  static initialize() {
    FormLayoutModel.modeModels[
      FormLayoutModes.SingleColumn
    ] = SingleColumnFormLayoutModel;
    FormLayoutModel.modeModels[
      FormLayoutModes.TwoColumn
    ] = TwoColumnFormLayoutModel;
  }

  constructor() { }
}

export class SingleColumnFormLayoutModel extends FormLayoutModel {
  columnEntities: DataEntityLayoutModel[];

  constructor(
    activity: FormActivity,
    options?: Partial<SingleColumnFormLayoutModel>
  ) {
    super();

    // Get the list of entities from the activity if we are switching from one layout to another
    if (activity && activity.model.getEntities) {
      this.columnEntities = activity.model
        .getEntities()
        .map((de: DataEntity, index: number) => {
          return new DataEntityLayoutModel({ entity: de, displayOrder: index });
        });
    }

    if (options) {
      Object.assign(this, options);

      if (options.columnEntities) {
        this.columnEntities = options.columnEntities.map(
          (de: DataEntityLayoutModel, index: number) => {
            if (de.type === 'table-data-entity-layout') {
              return new DataEntityTableLayoutModel(de as DataEntityTableLayoutModel);
            }
            else if (de.type === 'group-data-entity-layout') {
              return new DataEntityGroupLayoutModel(de as DataEntityGroupLayoutModel);
            }
            else if (de.type === 'row-data-entity-layout') {
              return new DataEntityRowLayoutModel(de as DataEntityRowLayoutModel);
            }
            else {
              return new DataEntityLayoutModel(de);
            }
          }
        );
      }
    }

    if (!this.columnEntities) {
      this.columnEntities = [];
    }
  }

  getEntities(): DataEntity[] {
    if (this.columnEntities) {
      let entities: DataEntity[] = [];
      this.columnEntities.forEach(de => {
        entities = entities.concat(de?.getDataEntities() || []);
      });

      return entities;
    }

    return [];
  }
  override addLayout(layoutModel: DataEntityLayoutModel) {
    this.columnEntities.push(layoutModel);
  }
  addEntity(de: DataEntity) {
    const entity = this.columnEntities.find(
      e => e.entity && e.entity.templateCode === de.templateCode
    );
    if (!entity) {
      this.columnEntities.push(
        new DataEntityLayoutModel({
          entity: de,
          displayOrder: this.columnEntities.length
        })
      );
    }
  }
  removeEntity(de: DataEntityLayoutModel) {
    const ceDE = de;

    if (ceDE) {
      const index = this.columnEntities.indexOf(ceDE);

      if (index > -1) {
        this.columnEntities.splice(index, 1);
      }
    }
  }
  clearEntities() {
    this.columnEntities.splice(0, this.columnEntities.length);
  }
}

export class TwoColumnFormLayoutModel extends FormLayoutModel {
  leftColumnEntities: DataEntityLayoutModel[] = [];
  rightColumnEntities: DataEntityLayoutModel[] = [];

  constructor(
    activity: FormActivity,
    options?: Partial<TwoColumnFormLayoutModel>
  ) {
    super();

    // Get the list of entities from the activity if we are switching from one layout to another
    if (activity && activity.model.getEntities) {
      this.leftColumnEntities = activity.model
        .getEntities()
        .map((de: DataEntity, index: number) => {
          return new DataEntityLayoutModel({ entity: de, displayOrder: 0 });
        });
    }

    if (options) {
      if (options.leftColumnEntities) {
        this.leftColumnEntities = options.leftColumnEntities.map(
          (de: DataEntityLayoutModel, index: number) => {
            return new DataEntityLayoutModel(de);
          }
        );
      }

      if (options.rightColumnEntities) {
        this.rightColumnEntities = options.rightColumnEntities.map(
          (de: DataEntityLayoutModel, index: number) => {
            return new DataEntityLayoutModel(de);
          }
        );
      }
    }
  }

  getEntities(): DataEntity[] {
    return (this.leftColumnEntities || [])
      .map(de => de.entity)
      .concat((this.rightColumnEntities || []).map(de => de.entity));
  }

  override addLayout(layoutModel: DataEntityLayoutModel) {
    this.leftColumnEntities.push(layoutModel);
  }
  addEntity(de: DataEntity) {
    const entity = this.leftColumnEntities.find(
      e => e.entity.templateCode === de.templateCode
    );
    if (!entity) {
      this.leftColumnEntities.push(
        new DataEntityLayoutModel({
          entity: de,
          displayOrder: this.leftColumnEntities.length
        })
      );
    }
  }

  removeEntity(de: DataEntityLayoutModel) {
    let ceDE = this.leftColumnEntities.find(
      lde => lde.entity.templateCode === de.entity.templateCode
    );

    if (ceDE) {
      const index = this.leftColumnEntities.indexOf(ceDE);

      if (index > -1) {
        this.leftColumnEntities.splice(index, 1);
      }
    }

    if (!ceDE) {
      ceDE = this.rightColumnEntities.find(
        rde => rde.entity.templateCode === de.entity.templateCode
      );

      if (ceDE) {
        const index = this.rightColumnEntities.indexOf(ceDE);

        if (index > -1) {
          this.rightColumnEntities.splice(index, 1);
        }
      }
    }
  }

  clearEntities() {
    this.leftColumnEntities.splice(0, this.leftColumnEntities.length);
    this.rightColumnEntities.splice(0, this.rightColumnEntities.length);
  }
}

// Static constructor on FormLayoutModel, this needs to be after any LayoutModel definition to make sure they are all defined with this is called
FormLayoutModel.initialize();

export class FormActivityModel extends ActivityModel {
  values: { [key: string]: string };
  formLayout: FormLayoutModes = FormLayoutModes.SingleColumn;
  formLayoutModel: FormLayoutModel = new SingleColumnFormLayoutModel(null);

  constructor(options?: Partial<FormActivityModel>) {
    super(options);

    if (options) {
      Object.assign(this, options);
    }

    this.formLayoutModel = FormLayoutModel.getModeModel(
      null,
      this.formLayout,
      this.formLayoutModel
    );
  }

  getEntities(): DataEntity[] {
    if (this.formLayoutModel) {
      return this.formLayoutModel.getEntities();
    }
    return [];
  }
}

export class FormActivity<
  T extends FormActivityModel = FormActivityModel
> extends ScreenActivity<FormActivityModel> {
  constructor(options?: Partial<FormActivity<T>>) {
    super(options);
    this.type = 'form-activity';
    this.name = 'Form';
    this.description = 'Collect required data via a form';

    if (options) {
      Object.assign(this, options);
    }

    this.model = new FormActivityModel(options ? options.model : null);

    this.model.screenName = this.model.screenName || 'Form';
  }

  public displayOnScreen(): boolean {
    return super.displayOnScreen();
  }


  override prepareForSave(): void {
    // Prepare the activity for saving
    // This is called before the workflow is saved
    super.prepareForSave();
    const entities = this.model?.getEntities();
    if (entities) {
      entities.forEach((de: DataEntity) => {
        de.prepareForSave();
      });
    }
  }

  getValues(): { [key: string]: string } {
    if (!this.model.values) {
      const values: { [key: string]: any } = {};

      (this.model as FormActivityModel).formLayoutModel
        .getEntities()
        .forEach((value: DataEntity, index: number) => {
          if (value.value && value.value !== '') {
            values[value.templateCode] = value.value;
          }
        });

      this.model.values = values;
    }

    return this.model.values;
  }

  addEntity(entity: DataEntity) {
    entity.parent = new ParentReference({ id: this.id });
    if (this.model.formLayoutModel) {
      this.model.formLayoutModel.addEntity(entity);
    }
  }
}
